import { Box, Group, Progress, Skeleton, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/clients/admin.client';
import { TokenHistory } from '@/clients/types';
import { fmtCurrency } from '@/lib/currency';

type UsageMeterProps = {
  label: string;
  used: number;
  limit?: number | null;
  format?: (v: number) => string;
  suffix?: string;
};

export function UsageMeter({ label, used, limit, format = String, suffix = '' }: UsageMeterProps) {
  return (
    <Box maw={480}>
      <Group justify="space-between">
        <Text size="sm">{label}</Text>
        <Text size="sm">
          {limit ? `${format(used)} / ${format(limit)}${suffix}` : `${format(used)}${suffix}`}
        </Text>
      </Group>
      {!!limit && (
        <>
          <Progress value={Math.min((used / limit) * 100, 100)} aria-label={label} mt={4} />
          <Text size="xs" c="dimmed" mt={4}>
            残り {format(Math.max(limit - used, 0))}
            {suffix}
          </Text>
        </>
      )}
    </Box>
  );
}

export function WebSearchUsage({ adminClient }: { adminClient: typeof api }) {
  const query = useQuery({
    queryKey: ['tenantWebSearchUsage'],
    queryFn: () => adminClient.settings_v1_get_tenant_web_search_usage(),
  });

  return (
    <Stack>
      <Title order={5}>Web 検索</Title>
      {query.isLoading && <Skeleton visible w="100%" h={80} opacity="30%" />}
      {query.isError && <div>Error</div>}
      {query.data && (
        <>
          <UsageMeter
            label="今日"
            used={query.data.daily_count}
            limit={query.data.daily_limit}
            suffix=" 回"
          />
          <UsageMeter
            label="今月"
            used={query.data.monthly_count}
            limit={query.data.monthly_limit}
            suffix=" 回"
          />
          <Text size="xs" c="dimmed">
            今日の回数は毎日 0:00 UTC（日本時間 9:00）に、今月の回数は毎月 1 日の同じ時刻に 0
            に戻ります。1 回の質問で複数回検索するため、残りが少ないと検索を始める前に止まります。
          </Text>
        </>
      )}
    </Stack>
  );
}

type MonthlySpendProps = {
  limit?: number | null;
  currency: string;
  queryKey: unknown[];
  // 今月の token-history の items を返す
  fetchItems: (year: number, month: number) => Promise<TokenHistory[]>;
};

// 月の利用上限があるときだけ、今月の利用額を上限と並べて出す
export function MonthlySpend({ limit, currency, queryKey, fetchItems }: MonthlySpendProps) {
  const query = useQuery({
    queryKey,
    enabled: !!limit,
    queryFn: async () => {
      const now = new Date();
      const items = await fetchItems(now.getFullYear(), now.getMonth() + 1);
      return items.reduce((sum, item) => sum + item.bill_spent, 0);
    },
  });

  if (!limit) {
    return null;
  }
  return (
    <>
      {query.isLoading && <Skeleton visible w="100%" h={40} opacity="30%" />}
      {query.isError && <div>Error</div>}
      {query.data !== undefined && (
        <UsageMeter
          label="今月の利用額"
          used={query.data}
          limit={limit}
          format={(v) => fmtCurrency(Math.round(v * 100) / 100, currency) ?? '0'}
        />
      )}
    </>
  );
}
